import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { TextFieldElement, useForm } from 'react-hook-form-mui'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'
import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'

import useHiddenFormElement from '../hooks/useHiddenFormElement'
import usePageErrorMessages from '../hooks/usePageErrorMessages'
import CancelButton from './CancelButton'
import useFormAnalytics from '../hooks/useFormAnalytics'
import FormContainer from './FormContainer'

const getChildInnerText = (element: HTMLElement | null) =>
  ([...(element?.children ?? [])] as HTMLElement[])
    .filter((e) => e?.style?.display !== 'none' && e?.ariaHidden !== 'true')
    .map((e) => e.innerText)

const joinChildInnerText = (items: string[]) => items.join('<br/>')

const sendCodeSchema = z.object({
  username: z.string().trim().email('Please enter a valid email address'),
})

const validateCodeSchema = z.object({
  username: z.string().trim().email('Please enter a valid email address'),
  code: z.string().regex(/^\d{6}$/, 'Your verification code should be 6 digits long'),
})

interface Fields {
  username: string
  code: string
}

const EmailVerification: React.FC<{ formName: string }> = ({ formName }) => {
  const [isCodeSent, setIsCodeSent] = useState(false)

  const infoMessagesElement = useHiddenFormElement('.verificationInfoText')
  const infoMessages = useMemo(() => joinChildInnerText(getChildInnerText(infoMessagesElement)), [infoMessagesElement])
  const successMessagesElement = useHiddenFormElement('.verificationSuccessText')
  const successMessages = useMemo(
    () =>
      joinChildInnerText(
        getChildInnerText(successMessagesElement).filter(
          (message) => isCodeSent || !message.includes('Verification code has been sent'),
        ),
      ),
    [successMessagesElement, isCodeSent],
  )
  const errorMessagesElement = useHiddenFormElement('.verificationErrorText')
  const errorMessages = useMemo(
    () => joinChildInnerText(getChildInnerText(errorMessagesElement)),
    [errorMessagesElement],
  )
  const pageErrorMessages = usePageErrorMessages()

  const resolver = useMemo(
    () => (isCodeSent ? zodResolver(validateCodeSchema) : zodResolver(sendCodeSchema)),
    [isCodeSent],
  )
  const [initialEmailInputValue] = useState(document.querySelector<HTMLInputElement>('#email')?.value)
  const formContext = useForm<Fields>({
    resolver,
    defaultValues: {
      username: initialEmailInputValue,
    },
  })
  const { resetField } = formContext

  const analytics = useFormAnalytics(`${formName}_email_verification`, errorMessages)

  // If the email is changed after sending the code, we revert to just sending a code for the current email
  const { watch } = formContext
  const username = watch('username')
  const verificationCodeInput = useHiddenFormElement('#VerificationCode') as HTMLInputElement
  const emailInput = useHiddenFormElement('#email') as HTMLInputElement
  useEffect(
    () =>
      setIsCodeSent((wasCodeSent) => {
        const isCodeSentNow = !(verificationCodeInput?.disabled ?? true) && emailInput.value === username
        if (wasCodeSent && !isCodeSentNow) {
          resetField('code')
        }
        return isCodeSentNow
      }),
    [verificationCodeInput, emailInput, username, resetField],
  )

  const sendCode = useCallback(
    ({ username }: Fields) => {
      document.querySelector<HTMLInputElement>('#email')!.value = username
      document.querySelector<HTMLButtonElement>('#EmailVerificationControl_but_send_code')!.click()
      analytics.onOther('send_code')
    },
    [analytics],
  )

  const verifyCode = useCallback(
    ({ code }: Fields) => {
      document.querySelector<HTMLInputElement>('#VerificationCode')!.value = code
      document.querySelector<HTMLButtonElement>('#EmailVerificationControl_but_verify_code')!.click()
      analytics.onOther('verify_code')
    },
    [analytics],
  )

  const retry = useCallback(() => window.location.reload(), [])

  const sendNewCode = useCallback(() => {
    resetField('code')
    document.querySelector<HTMLButtonElement>('#EmailVerificationControl_but_send_new_code')!.click()
    analytics.onOther('send_new_code')
  }, [analytics, resetField])

  const [isContinuing, setIsContinuing] = React.useState(false)
  useEffect(
    () => setIsContinuing((wasContinuing) => wasContinuing || successMessages.includes('You can now continue')),
    [successMessages],
  )

  const hasContinued = React.useRef(false)
  useEffect(() => {
    if (isContinuing && !hasContinued.current) {
      hasContinued.current = true
      const disableMetaEvents = process.env.REACT_APP_DISABLE_META_EVENTS === 'true'
      if (!disableMetaEvents) {
        fbq('trackCustom', 'userData', { email: username })
      }
      gtag('set', 'user_data', { email: username })
      analytics.onOther('code_verified')
      document.querySelector<HTMLButtonElement>('#continue')!.click()
    }
  }, [analytics, isContinuing, username])

  const [hasFailed, setHasFailed] = React.useState(false)
  useEffect(() => setHasFailed((hadFailed) => hadFailed || pageErrorMessages.length > 0), [pageErrorMessages])

  const workingElement = useHiddenFormElement('.working')
  const isLoading = useMemo(() => workingElement?.getAttribute('aria-hidden') === 'false', [workingElement])

  const showLoadingSpinner = useMemo(() => isContinuing && !hasFailed, [isContinuing, hasFailed])
  if (showLoadingSpinner) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (hasFailed) {
    return (
      <Stack spacing={2}>
        {pageErrorMessages && (
          <Typography component="div" color="error" dangerouslySetInnerHTML={{ __html: pageErrorMessages }} />
        )}
        {errorMessages && (
          <Typography component="div" color="error" dangerouslySetInnerHTML={{ __html: errorMessages }} />
        )}
        <LoadingButton id="retry" fullWidth variant="contained" color="primary" onClick={retry}>
          Try Again
        </LoadingButton>
        <CancelButton variant="outlined" fullWidth />
      </Stack>
    )
  }

  return (
    <>
      <FormContainer
        formContext={formContext}
        onSuccess={isCodeSent ? verifyCode : sendCode}
        onError={analytics.onError}
        onBlur={analytics.onBlur}
      >
        <Stack>
          <Stack mb={6}>
            <TextFieldElement
              type="email"
              name="username"
              label="Email Address"
              autoComplete="username"
              autoFocus
              required
              disabled={isLoading || hasFailed || Boolean(initialEmailInputValue)}
            />
            {!hasFailed && !isLoading && infoMessages && (
              <Typography my={2} variant="caption" dangerouslySetInnerHTML={{ __html: infoMessages }} />
            )}
            {!hasFailed && !isLoading && successMessages && (
              <Typography my={2} variant="caption" dangerouslySetInnerHTML={{ __html: successMessages }} />
            )}
            {isCodeSent && (
              <TextFieldElement
                type="text"
                name="code"
                label="Verification Code"
                autoComplete="one-time-code"
                required
                disabled={isLoading || hasFailed}
              />
            )}
          </Stack>
          {pageErrorMessages && (
            <Typography component="div" mb={2} color="error" dangerouslySetInnerHTML={{ __html: pageErrorMessages }} />
          )}
          {errorMessages && (
            <Typography component="div" mb={2} color="error" dangerouslySetInnerHTML={{ __html: errorMessages }} />
          )}
          <Stack spacing={2}>
            <LoadingButton fullWidth type="submit" variant="contained" loading={isLoading}>
              {isCodeSent ? 'Verify Code' : 'Send Verification Code'}
            </LoadingButton>
            {isCodeSent && (
              <LoadingButton
                fullWidth
                type="button"
                variant="text"
                color="secondary"
                loading={isLoading}
                onClick={sendNewCode}
              >
                Send New Code
              </LoadingButton>
            )}
          </Stack>
        </Stack>
      </FormContainer>
    </>
  )
}

export default EmailVerification
